import { GetString } from "fluent-react";
import max from "lodash-es/max";
import min from "lodash-es/min";
import React, { RefObject } from "react";
import styled from "styled-components";
import {
  Product,
  ProductClass,
  ProductLevel,
} from "../../graphQL/types/shared";
import {
  CountryProductLookback,
  CountryProductYear,
  LookBackYearRange,
  ProductYear,
} from "../../graphQL/types/shared";
import { gql, useQuery } from "../../graphQL/useQuery";
import { generateStringProductId } from "../../graphQL/Utils";
import { SlideElementProps } from "../../presentationManager/PresentationManager";
import { separationBetweenControlDropdowns } from "../exportComplexity/Utils";
import { width as graphControlDropdownWidth } from "../graphControlDropdown";
import { GraphControlsContainer } from "../Grid";
import { ExportsChangeNodeSizing as NodeSizing } from "../Utils";
import Legend from "./Legend";

const legendWidth = "10rem";

//#region Styling
const separationFromLeftDropdown = 1; // in rem
const Root = styled(GraphControlsContainer)`
  margin-left: ${graphControlDropdownWidth * 2 +
    separationBetweenControlDropdowns +
    separationFromLeftDropdown}rem;
  width: ${legendWidth};
`;
//#endregion

const query = gql`
  query ExportGrowthDynamicsLegend(
    $year: Int!
    $id: ID!
    $productLevel: ProductLevel!
    $yearRange: LookBackYearRange!
  ) {
    countryProductLookback(
      location: $id
      yearRange: $yearRange
      productLevel: $productLevel
    ) {
      product {
        id
        topLevelParent {
          id
        }
      }
      exportGrowthCAGR: exportValueConstCagr
    }

    allCountryProductYear(
      location: $id
      year: $year
      productClass: HS
      productLevel: $productLevel
    ) {
      product {
        id
      }
      exportValue
    }

    allProductYear(productClass: HS, productLevel: $productLevel, year: $year) {
      product {
        id
      }
      globalExportValue
      pci
    }
  }
`;

interface Variables {
  year: number;
  id: string;
  productLevel: ProductLevel;
  yearRange: LookBackYearRange;
}
interface SuccessResponse {
  countryProductLookback: Array<{
    product: {
      id: string;
      topLevelParent: { id: string };
    };
    exportGrowthCAGR: number;
  }>;

  allCountryProductYear: Array<{
    product: { id: string };
    exportValue: number | null;
  }>;

  allProductYear: Array<{
    product: { id: string };
    globalExportValue: number | null;
    pci: number | null;
  }>;
}

const transform = (
  data: SuccessResponse,
  nodeSizing: NodeSizing.CountryTrade | NodeSizing.WorldTrade
) => {
  const tradeValues: number[] = [];
  const otherCategoryID = generateStringProductId({
    productClass: ProductClass.HS,
    id: 10,
  });
  for (const { product, exportGrowthCAGR } of data.countryProductLookback) {
    const {
      topLevelParent: { id: category },
    } = product;
    const countryProductYearDatum = data.allCountryProductYear.find(
      (d) => d.product.id === product.id
    );
    const exportValue =
      countryProductYearDatum && countryProductYearDatum.exportValue !== null
        ? countryProductYearDatum.exportValue
        : null;
    const productYearDatum = data.allProductYear.find(
      (d) => d.product.id === product.id
    );
    const globalExportValue =
      productYearDatum && productYearDatum.globalExportValue !== null
        ? productYearDatum.globalExportValue
        : null;
    const pci =
      productYearDatum && productYearDatum.pci !== null
        ? productYearDatum.pci
        : null;
    // Note: the filtering done here needs to match the filtering done in the
    // graph's transforming funcftion to ensure consistency between the radius
    // scale here and the one used in the chart:
    if (
      pci !== null &&
      exportGrowthCAGR !== null &&
      category !== otherCategoryID
    ) {
      if (nodeSizing === NodeSizing.CountryTrade && exportValue !== null) {
        tradeValues.push(exportValue);
      } else if (
        nodeSizing === NodeSizing.WorldTrade &&
        globalExportValue !== null
      ) {
        tradeValues.push(globalExportValue);
      }
    }
  }
  const maxTradeValue = max(tradeValues);
  const minTradeValue = min(tradeValues);
  if (maxTradeValue === undefined || minTradeValue === undefined) {
    throw new Error("tradeValues is empty array");
  }
  return {
    max: maxTradeValue,
    min: minTradeValue,
  };
};
interface Props extends SlideElementProps {
  year: number;
  location: string;
  yearRange: LookBackYearRange;
  zIndex: number;
  getFluentString: GetString;
  nodeSizing: NodeSizing;
}

const LegendWrapper = React.forwardRef(
  (props: Props, rootElRef: RefObject<HTMLDivElement>) => {
    const {
      location,
      yearRange,
      year,
      zIndex,
      getFluentString,
      nodeSizing,
    } = props;
    const variables: Variables = {
      yearRange,
      id: location,
      year,
      productLevel: ProductLevel.twoDigit,
    };

    const { loading, error, data } = useQuery<SuccessResponse, Variables>(
      query,
      { variables }
    );
    let output: React.ReactElement<any> | null;
    if (loading === true) {
      output = null;
    } else if (error !== undefined) {
      console.error(error);
      output = null;
    } else if (data !== undefined) {
      if (
        nodeSizing === NodeSizing.CountryTrade ||
        nodeSizing === NodeSizing.WorldTrade
      ) {
        const transformed = transform(data, nodeSizing);
        output = (
          <Legend
            min={transformed.min}
            max={transformed.max}
            getFluentString={getFluentString}
          />
        );
      } else {
        output = null;
      }
    } else {
      output = null;
    }

    const style: React.CSSProperties = {
      zIndex,
    };

    return (
      <Root style={style} ref={rootElRef}>
        {output}
      </Root>
    );
  }
);

export default LegendWrapper;
